import { useState } from 'react';
import PropTypes from 'prop-types';

import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import TagInput from '#/modules/commonComponents/form/TagInput';
import {
  joinAccessories,
  parseAccessories,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/accessoriesTags';

/**
 * 更正序號（mode=serial）或新增品項（mode=add）。兩者皆需原因；新增另需問題描述。
 * 開啟時才掛載 session，欄位自然重置。
 */
const LineCommandSession = ({
  open,
  onOpenChange,
  mode,
  currentSerialNo,
  isPending,
  onSubmit,
}) => {
  const isAdd = mode === 'add';
  const [serialNo, setSerialNo] = useState(isAdd ? '' : (currentSerialNo ?? ''));
  const [problemDescription, setProblemDescription] = useState('');
  const [accessories, setAccessories] = useState('');
  const [reason, setReason] = useState('');

  const trimmedSerial = serialNo.trim();
  const canSubmit = Boolean(trimmedSerial)
    && Boolean(reason.trim())
    && (!isAdd || Boolean(problemDescription.trim()))
    && (isAdd || trimmedSerial !== String(currentSerialNo ?? '').trim());

  function handleSubmit() {
    if (!canSubmit || isPending) {
      return;
    }
    onSubmit?.(isAdd
      ? { serialNo: trimmedSerial, problemDescription, accessories, reason }
      : { serialNo: trimmedSerial, reason });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isAdd ? '新增報修品項' : '更正產品序號'}
      description={isAdd
        ? '新增的品項留在原單，立即建立待處理維修品。'
        : '有維修報告草稿時需先取消草稿。單號、查詢碼不變，新序號履歷會顯示這張報修。'}
      closeOnOutsideClick={false}
      footer={(
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button type="button" disabled={!canSubmit || isPending} onClick={handleSubmit}>
            {isPending ? '送出中…' : (isAdd ? '新增' : '更正')}
          </Button>
        </DialogFooter>
      )}
    >
      <div className="space-y-3 pt-4">
        {isAdd ? null : (
          <FloatingInput
            id="repair-line-current-serial"
            type="text"
            label="目前序號"
            value={currentSerialNo ?? ''}
            readOnly
          />
        )}
        <FloatingInput
          id="repair-line-serial"
          type="text"
          label={isAdd ? '產品序號' : '正確序號'}
          value={serialNo}
          onChange={(event) => setSerialNo(event.target.value)}
        />
        {isAdd ? (
          <>
            <FloatingTextarea
              id="repair-line-problem"
              label="問題描述"
              value={problemDescription}
              onChange={(event) => setProblemDescription(event.target.value)}
              rows={2}
            />
            <TagInput
              id="repair-line-accessories"
              label="配件（輸入後按 Enter 新增，可多個）"
              value={parseAccessories(accessories)}
              onChange={(tags) => setAccessories(joinAccessories(tags))}
            />
          </>
        ) : null}
        <FloatingTextarea
          id="repair-line-reason"
          label="原因（必填）"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
        />
      </div>
    </AppDialog>
  );
};

LineCommandSession.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['serial', 'add']).isRequired,
  currentSerialNo: PropTypes.string,
  isPending: PropTypes.bool,
  onSubmit: PropTypes.func,
};

const RepairRequestLineCommandDialog = (props) => {
  const { open, onOpenChange, mode } = props;
  if (!open) {
    return (
      <AppDialog
        open={false}
        onOpenChange={onOpenChange}
        title={mode === 'add' ? '新增報修品項' : '更正產品序號'}
      />
    );
  }
  return <LineCommandSession {...props} />;
};

RepairRequestLineCommandDialog.propTypes = LineCommandSession.propTypes;

export default RepairRequestLineCommandDialog;
